import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  TrendingUp, Users, DollarSign, Target, Award, Shield, Star,
  UserPlus, Briefcase, BookOpen, Headphones, Bell, Settings,
  ChevronRight, ArrowUpRight, ArrowDownRight, Plus, Search,
  Filter, Calendar, Mail, Phone, Building2, ExternalLink,
  BarChart3, PieChart, Activity, Zap, Gift, MessageSquare,
  FileText, Video, Download, Globe, Clock, CheckCircle,
  AlertCircle, X, Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_TYPE_CONFIG = {
  credibility: { name: "Credibility Partner", color: "#8B5CF6", icon: Award },
  client_network: { name: "Client Network Partner", color: "#3B82F6", icon: Users },
  advisor: { name: "Advisor Partner", color: "#F59E0B", icon: Award },
  implementation: { name: "Implementation Partner", color: "#10B981", icon: Settings },
  referral: { name: "Referral Partner", color: "#EC4899", icon: Gift },
  channel: { name: "Channel Partner", color: "#EF4444", icon: TrendingUp }
};

const TIER_CONFIG = {
  registered: { name: "Registered", color: "#6B7280", icon: CheckCircle, nextTier: "authorized" },
  authorized: { name: "Authorized", color: "#3B82F6", icon: Shield, nextTier: "strategic" },
  strategic: { name: "Strategic", color: "#F59E0B", icon: Star, nextTier: null }
};

const PartnerPortalDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [resources, setResources] = useState([]);
  
  // Get partner info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const partnerId = user.partner_id;

  useEffect(() => {
    if (partnerId) {
      fetchDashboardData();
      fetchResources();
    }
  }, [partnerId]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API}/partners/portal/dashboard/${partnerId}`);
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch(`${API}/partners/portal/resources`);
      const data = await response.json();
      setResources(data.resources || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const partner = dashboardData?.partner || {};
  const metrics = dashboardData?.metrics || {};
  const tierInfo = dashboardData?.tier_info || TIER_CONFIG.registered;
  const partnerTypeInfo = dashboardData?.partner_type_info || {};

  // Navigation tabs
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: UserPlus },
    { id: 'deals', label: 'Deals', icon: Briefcase },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'communications', label: 'Messages', icon: MessageSquare },
    { id: 'support', label: 'Support', icon: Headphones }
  ];

  // Sidebar Component
  const Sidebar = () => (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-gradient-to-b from-slate-900 to-slate-800 text-white z-40">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <span className="text-xl font-black text-indigo-900">C</span>
          </div>
          <div>
            <h1 className="font-bold text-lg">CNESS</h1>
            <p className="text-xs text-white/60">Partner Portal</p>
          </div>
        </div>
      </div>
      
      {/* Partner Info */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-lg font-bold">
            {partner.first_name?.[0]}{partner.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{partner.full_name || partner.contact_name}</p>
            <p className="text-xs text-white/60 truncate">{partner.organization_name}</p>
          </div>
        </div>
        {/* Show Partnership Type as "Certified Partner - [Type]" */}
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <p className="text-xs text-white/60">Certified Partner</p>
          <p className="text-sm font-semibold text-amber-400">
            {partner.partnership_type || partnerTypeInfo.name || 'Partner'}
          </p>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="p-4 space-y-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === tab.id 
                ? 'bg-white/10 text-white' 
                : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            <span className="font-medium">{tab.label}</span>
          </button>
        ))}
      </nav>
      
      {/* Tier Progress */}
      {tierInfo.nextTier && (
        <div className="absolute bottom-20 left-4 right-4 p-4 bg-white/5 rounded-xl">
          <p className="text-xs text-white/60 mb-2">Progress to {TIER_CONFIG[tierInfo.nextTier]?.name}</p>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              style={{ width: '45%' }}
            />
          </div>
          <p className="text-xs text-white/40 mt-1">45% complete</p>
        </div>
      )}
      
      {/* Settings */}
      <div className="absolute bottom-4 left-4 right-4">
        <button className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 rounded-xl transition-all">
          <Settings className="w-5 h-5" />
          <span className="font-medium">Settings</span>
        </button>
      </div>
    </aside>
  );

  // Header Component
  const Header = () => (
    <header className="fixed top-0 left-64 right-0 h-16 bg-white border-b z-30 flex items-center justify-between px-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">
          {tabs.find(t => t.id === activeTab)?.label}
        </h2>
      </div>
      <div className="flex items-center gap-4">
        <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>
        <div className="flex items-center gap-3 pl-4 border-l">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-sm font-bold">
            {partner.first_name?.[0]}{partner.last_name?.[0]}
          </div>
        </div>
      </div>
    </header>
  );

  // Dashboard Content
  const DashboardContent = () => (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative z-10">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {partner.first_name}!</h1>
          <p className="text-white/80 mb-6">Here's what's happening with your partnership today.</p>
          <div className="flex gap-4">
            <Button 
              onClick={() => setShowLeadModal(true)}
              className="bg-white text-purple-700 hover:bg-gray-100"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Submit Lead
            </Button>
            <Button 
              onClick={() => setShowDealModal(true)}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Briefcase className="w-4 h-4 mr-2" /> Register Deal
            </Button>
          </div>
        </div>
      </div>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            label: 'Total Leads', 
            value: metrics.total_leads || 0, 
            change: '+12%', 
            positive: true,
            icon: UserPlus,
            color: 'from-blue-500 to-cyan-500'
          },
          { 
            label: 'Conversion Rate', 
            value: `${metrics.conversion_rate || 0}%`, 
            change: '+5%', 
            positive: true,
            icon: Target,
            color: 'from-green-500 to-emerald-500'
          },
          { 
            label: 'Active Deals', 
            value: metrics.total_deals || 0, 
            change: '+3', 
            positive: true,
            icon: Briefcase,
            color: 'from-purple-500 to-pink-500'
          },
          { 
            label: 'Pending Commission', 
            value: `$${(metrics.pending_commission || 0).toLocaleString()}`, 
            change: null,
            icon: DollarSign,
            color: 'from-yellow-500 to-orange-500'
          }
        ].map((metric, i) => (
          <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center`}>
                  <metric.icon className="w-6 h-6 text-white" />
                </div>
                {metric.change && (
                  <span className={`flex items-center text-sm font-medium ${metric.positive ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.positive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {metric.change}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</p>
              <p className="text-sm text-gray-500">{metric.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Quick Actions & Recent Activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'Submit New Lead', icon: UserPlus, action: () => setShowLeadModal(true), color: 'text-blue-600' },
              { label: 'Register Deal', icon: Briefcase, action: () => setShowDealModal(true), color: 'text-purple-600' },
              { label: 'View Resources', icon: BookOpen, action: () => setActiveTab('resources'), color: 'text-green-600' },
              { label: 'Get Support', icon: Headphones, action: () => setActiveTab('support'), color: 'text-orange-600' }
            ].map((action, i) => (
              <button
                key={i}
                onClick={action.action}
                className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <action.icon className={`w-5 h-5 ${action.color}`} />
                  <span className="font-medium text-gray-700">{action.label}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
          </CardContent>
        </Card>
        
        {/* Recent Leads */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Leads</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('leads')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {(dashboardData?.recent_leads || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No leads submitted yet</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setShowLeadModal(true)}
                >
                  Submit Your First Lead
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboardData.recent_leads.map((lead, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{lead.company_name}</p>
                        <p className="text-sm text-gray-500">{lead.contact_name}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      lead.status === 'converted' ? 'bg-green-100 text-green-700' :
                      lead.status === 'qualified' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Partner Benefits & Resources Preview */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Your Benefits */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              Your Partner Benefits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {(tierInfo.benefits || partnerTypeInfo.benefits || []).slice(0, 5).map((benefit, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
            {tierInfo.nextTier && (
              <div className="mt-4 p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-purple-700">
                  <Star className="w-4 h-4 inline mr-1" />
                  Upgrade to <strong>{TIER_CONFIG[tierInfo.nextTier]?.name}</strong> to unlock more benefits!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Latest Resources */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Latest Resources
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('resources')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {resources.slice(0, 3).map((resource, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    resource.type === 'video' ? 'bg-red-100' :
                    resource.type === 'course' ? 'bg-green-100' :
                    'bg-blue-100'
                  }`}>
                    {resource.type === 'video' ? <Video className="w-5 h-5 text-red-600" /> :
                     resource.type === 'course' ? <BookOpen className="w-5 h-5 text-green-600" /> :
                     <FileText className="w-5 h-5 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{resource.title}</p>
                    <p className="text-sm text-gray-500">{resource.format}</p>
                  </div>
                  <Download className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Leads Content
  const LeadsContent = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input placeholder="Search leads..." className="pl-10 w-64" />
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" /> Filter
          </Button>
        </div>
        <Button onClick={() => setShowLeadModal(true)}>
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </div>
      
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          {(dashboardData?.recent_leads || []).length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <UserPlus className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-medium mb-2">No leads yet</h3>
              <p className="text-gray-400 mb-4">Start submitting leads to earn commissions</p>
              <Button onClick={() => setShowLeadModal(true)}>
                <Plus className="w-4 h-4 mr-2" /> Submit Your First Lead
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Company</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dashboardData.recent_leads.map((lead, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{lead.company_name}</td>
                    <td className="px-6 py-4 text-gray-600">{lead.contact_name}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100">
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Resources Content
  const ResourcesContent = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input placeholder="Search resources..." className="pl-10" />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.map((resource, i) => (
          <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
            <CardContent className="p-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                resource.type === 'video' ? 'bg-red-100' :
                resource.type === 'course' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                {resource.type === 'video' ? <Video className="w-6 h-6 text-red-600" /> :
                 resource.type === 'course' ? <BookOpen className="w-6 h-6 text-green-600" /> :
                 <FileText className="w-6 h-6 text-blue-600" />}
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                {resource.title}
              </h3>
              <p className="text-sm text-gray-500 mb-4">{resource.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{resource.format}</span>
                <Button variant="ghost" size="sm">
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Support Content
  const SupportContent = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Headphones className="w-5 h-5 text-purple-600" />
            Contact Partner Support
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Subject</Label>
            <Input placeholder="How can we help?" className="mt-1" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea placeholder="Describe your question or issue..." rows={5} className="mt-1" />
          </div>
          <Button className="w-full">
            <Send className="w-4 h-4 mr-2" /> Send Message
          </Button>
        </CardContent>
      </Card>
      
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Other Ways to Reach Us</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <Mail className="w-5 h-5 text-gray-500" />
            <div>
              <p className="font-medium">Email Support</p>
              <p className="text-sm text-gray-500">partners@cness.cloud</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <Phone className="w-5 h-5 text-gray-500" />
            <div>
              <p className="font-medium">Phone Support</p>
              <p className="text-sm text-gray-500">+1 (555) 123-4567</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Lead Modal
  const LeadModal = () => {
    const [leadData, setLeadData] = useState({
      company_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      notes: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        const response = await fetch(`${API}/partners/portal/leads?partner_id=${partnerId}&company_name=${encodeURIComponent(leadData.company_name)}&contact_name=${encodeURIComponent(leadData.contact_name)}&contact_email=${encodeURIComponent(leadData.contact_email)}&contact_phone=${encodeURIComponent(leadData.contact_phone)}&notes=${encodeURIComponent(leadData.notes)}`, {
          method: 'POST'
        });
        if (response.ok) {
          setShowLeadModal(false);
          fetchDashboardData();
          alert('Lead submitted successfully!');
        }
      } catch (error) {
        alert('Error submitting lead');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowLeadModal(false)} />
        <Card className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Submit New Lead</CardTitle>
            <button onClick={() => setShowLeadModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input 
                  value={leadData.company_name}
                  onChange={(e) => setLeadData({...leadData, company_name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input 
                  value={leadData.contact_name}
                  onChange={(e) => setLeadData({...leadData, contact_name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input 
                  type="email"
                  value={leadData.contact_email}
                  onChange={(e) => setLeadData({...leadData, contact_email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input 
                  value={leadData.contact_phone}
                  onChange={(e) => setLeadData({...leadData, contact_phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={leadData.notes}
                  onChange={(e) => setLeadData({...leadData, notes: e.target.value})}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Lead'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading partner dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <Header />
      
      <main className="ml-64 pt-16 p-6">
        {activeTab === 'dashboard' && <DashboardContent />}
        {activeTab === 'leads' && <LeadsContent />}
        {activeTab === 'deals' && <LeadsContent />}
        {activeTab === 'resources' && <ResourcesContent />}
        {activeTab === 'communications' && <SupportContent />}
        {activeTab === 'support' && <SupportContent />}
      </main>
      
      {showLeadModal && <LeadModal />}
    </div>
  );
};

export default PartnerPortalDashboard;
